import { Button, Flex, Group, Modal, Select, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconAlertTriangleFilled } from '@tabler/icons-react';
import { schemaResolver, useForm } from '@mantine/form';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { User } from 'src/admin/users/types';
import { CreateUserData } from 'src/admin/users/context/UsersContext';
import CreateUserForm, { CreateUserFormValues } from 'src/admin/users/components/CreateUserForm';
import { formSchema, isDuplicateEmail } from 'src/admin/users/components/CreateUserModal';
import UserCreatedModal from 'src/admin/users/components/UserCreatedModal';
import { useHandleCreateUser } from 'src/admin/users/hooks/useHandleCreateUser';
import { useCompanies } from 'src/secret-menu/hooks/useCompanies';
import { useIsAfternoon } from 'src/common/useIsAfternoonHook';
import { api } from 'src/common/utils/api';

type FormValues = CreateUserFormValues & { companyId: string | null };

const AFTERNOON_DOMAIN = 'afternoon.co.uk';

function getEmailDomain(email: string): string {
  return email.split('@')[1]?.toLowerCase() ?? '';
}

function getCompanyDomains(users: User[]): string[] {
  const domains = users
    .map(u => getEmailDomain(u.emailAddress))
    .filter((domain): domain is string => !!domain && domain !== AFTERNOON_DOMAIN);
  return [...new Set(domains)];
}

type Props = {
  opened: boolean;
  close: () => void;
};

export default function SecretMenuAddUserModal({ opened, close }: Props) {
  const { companies } = useCompanies();
  const { isAfternoonAdmin } = useIsAfternoon();
  const queryClient = useQueryClient();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [domainMismatchWarning, setDomainMismatchWarning] = useState<string | null>(null);
  const [pendingValues, setPendingValues] = useState<CreateUserFormValues | null>(null);

  const sortedCompanies = [...(companies ?? [])].sort((a, b) => a.name.localeCompare(b.name));

  const form = useForm<FormValues>({
    mode: 'controlled',
    initialValues: {
      companyId: null,
      firstName: '',
      lastName: '',
      emailAddress: '',
      userRole: 'ADVISOR',
    },
    validate: values => {
      const zodErrors = schemaResolver(formSchema, { sync: true })(values);
      if (!values.companyId) {
        zodErrors.companyId = 'Please select a company';
      }
      if (!zodErrors.emailAddress && isDuplicateEmail(companyUsers, values.emailAddress)) {
        zodErrors.emailAddress = 'This email address is already in use.';
      }
      return zodErrors;
    },
    validateInputOnBlur: true,
  });

  const selectedCompanyId = form.values.companyId;
  const selectedCompany = sortedCompanies.find(c => c.id === selectedCompanyId);

  const { data: companyUsers = [], isLoading: isCompanyUsersLoading } = useQuery<User[]>({
    queryKey: ['admin', 'companies', selectedCompanyId, 'users'],
    enabled: !!selectedCompanyId,
  });

  const { mutateAsync: createUser, isPending: isCreating } = useMutation<
    { data: User },
    Error,
    { companyId: string; userData: CreateUserData }
  >({
    mutationFn: ({ companyId, userData }) =>
      api.post<User>(`admin/companies/${companyId}/users`, userData),
    onSuccess: (_, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies', companyId, 'users'] });
    },
  });

  const handleCreateUser = useHandleCreateUser(form, () => setShowSuccessModal(true));

  const doCreate = async (userValues: CreateUserFormValues) => {
    if (!selectedCompanyId) return;
    await handleCreateUser(
      () =>
        createUser({
          companyId: selectedCompanyId,
          userData: { ...userValues, userStatus: 'PENDING' } as CreateUserData,
        }),
      userValues,
    );
  };

  const handleSubmit = async (values: FormValues) => {
    const { companyId: _companyId, ...userValues } = values;
    const domains = getCompanyDomains(companyUsers);
    const newDomain = getEmailDomain(values.emailAddress);

    if (domains.length > 0 && !domains.includes(newDomain)) {
      setPendingValues(userValues as CreateUserFormValues);
      setDomainMismatchWarning(
        `The email domain "@${newDomain}" doesn't match the existing domains for ${selectedCompany?.name}: ${domains.map(d => `@${d}`).join(', ')}.`,
      );
      return;
    }

    await doCreate(userValues as CreateUserFormValues);
  };

  const handleConfirmDomainMismatch = async () => {
    if (!pendingValues) return;
    const values = pendingValues;
    setDomainMismatchWarning(null);
    setPendingValues(null);
    await doCreate(values);
  };

  const handleClose = () => {
    form.reset();
    close();
  };

  const showSecretMenuAccessOption =
    selectedCompany?.tenantName === 'afternoon' && !!isAfternoonAdmin;

  return (
    <>
      <Modal opened={opened} onClose={handleClose} title="Invite new user" centered>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Flex direction="column" gap="md">
            <Select
              label="Company"
              placeholder="Select a company"
              data={sortedCompanies.map(c => ({ label: c.name, value: c.id }))}
              searchable
              {...form.getInputProps('companyId')}
            />
            <CreateUserForm form={form} showSecretMenuAccessOption={showSecretMenuAccessOption} />
          </Flex>
          <Group justify="flex-end" mt="md">
            <Button
              type="submit"
              variant="filled-pill-black"
              disabled={!form.isValid() || isCreating || isCompanyUsersLoading}
              loading={isCreating}
            >
              Add
            </Button>
          </Group>
        </form>
      </Modal>

      <UserCreatedModal
        opened={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          close();
        }}
      />

      <Modal
        opened={!!domainMismatchWarning}
        onClose={() => {
          setDomainMismatchWarning(null);
          setPendingValues(null);
        }}
        title="Email domain mismatch"
        centered
      >
        <Stack align="center" mb="md">
          <ThemeIcon color="red" size={64} radius="xl" variant="light">
            <IconAlertTriangleFilled size={40} />
          </ThemeIcon>
        </Stack>
        <Text mb="md">{domainMismatchWarning}</Text>
        <Text mb="md" fw={700}>
          Are you sure you want to add this user?
        </Text>
        <Group justify="flex-end">
          <Button
            variant="default"
            onClick={() => {
              setDomainMismatchWarning(null);
              setPendingValues(null);
            }}
          >
            Cancel
          </Button>
          <Button color="red" onClick={handleConfirmDomainMismatch} loading={isCreating}>
            Proceed anyway
          </Button>
        </Group>
      </Modal>
    </>
  );
}
